import {
  DEFAULT_CUTOFF_MINUTES,
  DEFAULT_WINDOWS,
} from '../constants/windows';
import {
  RESULTS_WAIT_REOPEN_MINUTES,
  resultsBlocksFromAcceptance,
  type ResultsWaitBlock,
} from './sourceWindows';
import {
  addCubaCalendarDays,
  cubaDayKey,
  cubaMinutesOfDay,
  getCubaTimeParts,
} from '../time/cubaTime';

export interface WindowScheduleConfig {
  dayOpen?: string;
  dayClose?: string;
  nightOpen?: string;
  nightClose?: string;
  cutoffMin?: number;
}

export interface ResolvedWindowSchedule {
  dayOpen: string;
  dayClose: string;
  nightOpen: string;
  nightClose: string;
  cutoffMin: number;
  dayBlock: ResultsWaitBlock;
  nightBlock: ResultsWaitBlock;
  reopenAfterBlockMinutes: number;
}

/** Parse `HH:MM` to minutes since midnight (0–1439). */
export function parseTimeToMinutes(value: string): number {
  const [h, m] = value.split(':').map(Number);
  return h * 60 + m;
}

/** Add minutes to `HH:MM`, wrapping past midnight. */
export function addMinutesToTime(value: string, deltaMinutes: number): string {
  const total = (parseTimeToMinutes(value) + deltaMinutes + 24 * 60) % (24 * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** True when open is after close on the clock (acceptance spans two calendar days). */
export function windowCrossesMidnight(openHHMM: string, closeHHMM: string): boolean {
  return parseTimeToMinutes(openHHMM) > parseTimeToMinutes(closeHHMM);
}

/**
 * Acceptance interval. Overnight windows use open > close
 * (e.g. 22:00→02:00 → cur >= 22:00 OR cur < close−cutoff).
 */
export function isInAcceptanceWindow(
  cur: number,
  open: number,
  close: number,
  cutoff: number,
): boolean {
  const end = (close - cutoff + 24 * 60) % (24 * 60);
  if (open === close) return false;
  if (open < close) {
    // Same calendar day: [open, close−cutoff)
    return cur >= open && cur < close - cutoff;
  }
  // Overnight: [open, 24:00) ∪ [00:00, close−cutoff)
  if (cutoff >= close) {
    // Entire post-midnight leg is inside cutoff — only pre-midnight leg accepts.
    return cur >= open;
  }
  return cur >= open || cur < end;
}

/** Fill defaults and legacy gaps (night open = end of day wait block + reopen). */
export function resolveWindowSchedule(
  config: WindowScheduleConfig | null | undefined,
): ResolvedWindowSchedule {
  const reopen = DEFAULT_WINDOWS.reopenAfterBlockMinutes ?? RESULTS_WAIT_REOPEN_MINUTES;
  const dayClose = config?.dayClose ?? DEFAULT_WINDOWS.day.close;
  const nightClose = config?.nightClose ?? DEFAULT_WINDOWS.night.close;
  const dayOpen = config?.dayOpen ?? DEFAULT_WINDOWS.day.open;
  const nightOpen = config?.nightOpen
    ?? addMinutesToTime(
      dayClose,
      (DEFAULT_WINDOWS.resultsGapMinutes ?? 0) + reopen,
    );
  const blocks = resultsBlocksFromAcceptance(
    dayOpen,
    dayClose,
    nightOpen,
    nightClose,
    reopen,
  );
  return {
    dayOpen,
    dayClose,
    nightOpen,
    nightClose,
    cutoffMin: config?.cutoffMin ?? DEFAULT_CUTOFF_MINUTES,
    dayBlock: blocks.dayBlock,
    nightBlock: blocks.nightBlock,
    reopenAfterBlockMinutes: reopen,
  };
}

/** True when `cur` minutes-of-day falls inside [from, to) (wraps midnight). */
export function isInHalfOpenTimeRange(
  cur: number,
  from: number,
  to: number,
): boolean {
  if (from === to) return false;
  if (from < to) return cur >= from && cur < to;
  return cur >= from || cur < to;
}

/** Inside a planned results-wait block (no acceptance). */
export function isInResultsWaitBlock(
  config: WindowScheduleConfig | null | undefined,
  nowUtc: Date = new Date(),
): 'day' | 'night' | null {
  const schedule = resolveWindowSchedule(config);
  const cur = cubaMinutesOfDay(nowUtc);
  const dayFrom = parseTimeToMinutes(schedule.dayBlock.from);
  const dayTo = parseTimeToMinutes(schedule.dayBlock.to);
  const nightFrom = parseTimeToMinutes(schedule.nightBlock.from);
  const nightTo = parseTimeToMinutes(schedule.nightBlock.to);
  if (isInHalfOpenTimeRange(cur, dayFrom, dayTo)) return 'day';
  if (isInHalfOpenTimeRange(cur, nightFrom, nightTo)) return 'night';
  return null;
}

/** True when listers may submit plays right now. */
export function isWindowOpen(
  config: WindowScheduleConfig | null | undefined,
  nowUtc: Date = new Date(),
): boolean {
  return getActiveWindow(config, nowUtc) != null;
}

/** Active play window label, or null when closed. */
export function getActiveWindow(
  config: WindowScheduleConfig | null | undefined,
  nowUtc: Date = new Date(),
): 'day' | 'night' | null {
  const schedule = resolveWindowSchedule(config);
  const cur = cubaMinutesOfDay(nowUtc);
  const cutoff = schedule.cutoffMin;

  const dayOpen = parseTimeToMinutes(schedule.dayOpen);
  const dayClose = parseTimeToMinutes(schedule.dayClose);
  const nightOpen = parseTimeToMinutes(schedule.nightOpen);
  const nightClose = parseTimeToMinutes(schedule.nightClose);

  if (isInAcceptanceWindow(cur, dayOpen, dayClose, cutoff)) return 'day';
  if (isInAcceptanceWindow(cur, nightOpen, nightClose, cutoff)) return 'night';
  return null;
}

/**
 * Business dayKey for an acceptance window at `nowUtc`.
 * Overnight post-midnight leg keeps the open calendar day (previous Cuba date).
 */
export function resolveAcceptanceDayKey(
  openHHMM: string,
  closeHHMM: string,
  nowUtc: Date = new Date(),
): string {
  const todayKey = cubaDayKey(nowUtc);
  if (!windowCrossesMidnight(openHHMM, closeHHMM)) return todayKey;

  const close = parseTimeToMinutes(closeHHMM);
  const cur = cubaMinutesOfDay(nowUtc);
  // Post-midnight segment [00:00, close) still belongs to the previous open day.
  if (cur < close) {
    const p = getCubaTimeParts(nowUtc);
    const prev = addCubaCalendarDays(p.year, p.month, p.day, -1);
    return `${prev.year}-${String(prev.month).padStart(2, '0')}-${String(prev.day).padStart(2, '0')}`;
  }
  return todayKey;
}

/**
 * Results board dayKey: calendar day of acceptance close / publish.
 * Overnight day that opened yesterday evening still labels as today's day after midnight.
 */
export function resolveResultsPublishDayKey(
  openHHMM: string,
  closeHHMM: string,
  nowUtc: Date = new Date(),
): string {
  const todayKey = cubaDayKey(nowUtc);
  if (!windowCrossesMidnight(openHHMM, closeHHMM)) return todayKey;

  const open = parseTimeToMinutes(openHHMM);
  const cur = cubaMinutesOfDay(nowUtc);
  // Pre-midnight leg [open, 24:00): close is tomorrow → publish day = tomorrow.
  if (cur >= open) {
    const p = getCubaTimeParts(nowUtc);
    const next = addCubaCalendarDays(p.year, p.month, p.day, 1);
    return `${next.year}-${String(next.month).padStart(2, '0')}-${String(next.day).padStart(2, '0')}`;
  }
  // Post-midnight [00:00, open): still before tonight's open → close day is today.
  return todayKey;
}

/** Results focus dayKey for day/night at `nowUtc` (publish calendar day). */
export function resolveResultsFocusDayKey(
  config: WindowScheduleConfig | null | undefined,
  window: 'day' | 'night',
  nowUtc: Date = new Date(),
): string {
  const schedule = resolveWindowSchedule(config);
  if (window === 'day') {
    return resolveResultsPublishDayKey(schedule.dayOpen, schedule.dayClose, nowUtc);
  }
  return resolveResultsPublishDayKey(schedule.nightOpen, schedule.nightClose, nowUtc);
}

/** dayKey for the currently active window, or calendar today when closed. */
export function resolveActiveTurnDayKey(
  config: WindowScheduleConfig | null | undefined,
  window: 'day' | 'night',
  nowUtc: Date = new Date(),
): string {
  const schedule = resolveWindowSchedule(config);
  if (window === 'day') {
    return resolveAcceptanceDayKey(schedule.dayOpen, schedule.dayClose, nowUtc);
  }
  return resolveAcceptanceDayKey(schedule.nightOpen, schedule.nightClose, nowUtc);
}

export {
  resolveResultsWaitTurnDayKey,
  resolveClosedTurnAcceptanceDayKey,
  resolveActiveWindowOpenedAt,
  isStaleSettledSession,
  acceptanceDayKeyForPlatformDraw,
  platformDayKeyForAcceptanceTurn,
} from './windowTurnKeys';

/** Human span label; overnight shows both calendar sides. */
export function formatWindowSpanLabel(openHHMM: string, closeHHMM: string): string {
  if (windowCrossesMidnight(openHHMM, closeHHMM)) {
    return `${openHHMM} → ${closeHHMM} (+1d)`;
  }
  return `${openHHMM} → ${closeHHMM}`;
}

/** Results-wait block label (planned publish window). */
export function formatResultsWaitSpanLabel(fromHHMM: string, toHHMM: string): string {
  if (windowCrossesMidnight(fromHHMM, toHHMM)) {
    return `${fromHHMM}–${toHHMM} (+1d)`;
  }
  return `${fromHHMM}–${toHHMM}`;
}

/**
 * Validate schedule before persisting.
 * Overnight shifts (open > close) are allowed; zero-length is not.
 */
export function validateWindowSchedule(
  config: WindowScheduleConfig,
): 'error_shift_order' | 'error_night_before_day' | null {
  const schedule = resolveWindowSchedule(config);
  const dayOpen = parseTimeToMinutes(schedule.dayOpen);
  const dayClose = parseTimeToMinutes(schedule.dayClose);
  const nightOpen = parseTimeToMinutes(schedule.nightOpen);
  const nightClose = parseTimeToMinutes(schedule.nightClose);

  if (dayOpen === dayClose) return 'error_shift_order';
  if (nightOpen === nightClose) return 'error_shift_order';

  const dayOvernight = dayOpen > dayClose;
  const nightOvernight = nightOpen > nightClose;

  // Same-day night must start after same-day day close.
  if (!dayOvernight && !nightOvernight && nightOpen < dayClose) {
    return 'error_night_before_day';
  }

  // Overnight night: opens after day close, and post-midnight close must finish before day opens.
  if (!dayOvernight && nightOvernight) {
    if (nightOpen < dayClose) return 'error_night_before_day';
    if (nightClose > dayOpen) return 'error_night_before_day';
  }

  // Overnight day + same-day night: night sits in the gap after day close and before day open.
  if (dayOvernight && !nightOvernight) {
    if (nightOpen < dayClose || nightClose > dayOpen) return 'error_night_before_day';
  }

  return null;
}

/** Roles allowed to edit house play-window config (mirrors Firestore rules). */
export function canEditPlayWindows(role: string | undefined): boolean {
  return role === 'banker' || role === 'god';
}
